/* eslint-disable no-magic-numbers */
import React, { useRef, useImperativeHandle, forwardRef, useMemo, useState } from 'react';
import { glyphsDataSelector } from '@/redux/glyphs/glyphs.selectors';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { BASE_NEW_API_URL } from '@/constants';
import { apiPath } from '@/redux/apiPath';
import { Input } from '@/shared/Input';
import Image from 'next/image';
import { Glyph } from '@/types/models';
import { Autocomplete } from '@/shared/Autocomplete';
import { Button } from '@/shared/Button';
import { addGlyph } from '@/redux/glyphs/glyphs.thunks';
import {
  getModelElementsForModel,
  updateElementGlyph,
} from '@/redux/modelElements/modelElements.thunks';
import { showToast } from '@/utils/showToast';
import { closeModal } from '@/redux/modal/modal.slice';
import { SerializedError } from '@reduxjs/toolkit';
import { elementGlyphStateSelector } from '@/redux/modal/modal.selector';
import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import { LoadingIndicator } from '@/shared/LoadingIndicator';

export type ElementGlyphFormHandles = {
  clearFileInput: () => void;
};

type LayerImageObjectFormProps = {
  selectedGlyph: number | null;
  setSelectedGlyph: (glyphId: number | null) => void;
  file: File | null;
  setFile: (file: File | null) => void;
};

export const ElementGlyphForm = forwardRef<ElementGlyphFormHandles, LayerImageObjectFormProps>(
  ({ selectedGlyph, setSelectedGlyph, file, setFile }, ref) => {
    const glyphs: Glyph[] = useAppSelector(glyphsDataSelector);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const initialSelectedGlyph = glyphs.find(glyph => glyph.id === selectedGlyph);

    const elementGlyphState = useAppSelector(elementGlyphStateSelector);

    const dispatch = useAppDispatch();

    const [isSending, setIsSending] = useState<boolean>(false);

    useImperativeHandle(ref, () => ({
      clearFileInput: (): void => {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      },
    }));

    const previewUrl: string | null = useMemo(() => {
      if (selectedGlyph) {
        return `${BASE_NEW_API_URL}${apiPath.getGlyphImage(selectedGlyph)}`;
      }
      if (file) {
        return URL.createObjectURL(file);
      }
      return null;
    }, [file, selectedGlyph]);

    const handleGlyphChange = (glyph: Glyph | null): void => {
      const glyphId = glyph?.id || null;
      setSelectedGlyph(glyphId);
      if (!glyphId && fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      const uploadedFile = e.target.files?.[0] || null;
      setFile(uploadedFile);
      if (uploadedFile && fileInputRef.current) {
        setSelectedGlyph(null);
      }
    };

    const handleSubmit = async (): Promise<void> => {
      if (!elementGlyphState) {
        return;
      }
      setIsSending(true);
      try {
        let glyphId = selectedGlyph;
        if (file) {
          const data = await dispatch(addGlyph(file)).unwrap();
          if (!data) {
            return;
          }
          glyphId = data.id;
        }
        await dispatch(
          updateElementGlyph({
            modelId: elementGlyphState.model,
            elementId: elementGlyphState.id,
            glyphId,
          }),
        ).unwrap();
        showToast({
          type: 'success',
          message: 'The element glyph has been successfully updated.',
        });
        dispatch(getModelElementsForModel(elementGlyphState.model));
        dispatch(closeModal());
      } catch (error) {
        const typedError = error as SerializedError;
        showToast({
          type: 'error',
          message: typedError.message || 'An error occurred while updating a glyph.',
        });
      } finally {
        setIsSending(false);
      }
    };

    return (
      <div>
        {isSending && (
          <div className="c-element-glyph-modal-loader">
            <LoadingIndicator width={44} height={44} />
          </div>
        )}
        <div className="grid grid-cols-3 gap-2">
          <div className="mb-4 flex flex-col gap-2">
            <span>Glyph:</span>
            <Autocomplete<Glyph>
              options={glyphs}
              initialValue={initialSelectedGlyph}
              clearable
              valueKey="id"
              labelKey="filename"
              onChange={handleGlyphChange}
            />
          </div>
          <div className="mb-4 flex flex-col gap-2">
            <span>File:</span>
            <Input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              data-testid="image-file-input"
              className="w-full border border-[#ccc] bg-white p-2"
            />
          </div>
          <div className="mb-4 flex flex-col gap-2 align-bottom">
            <Button
              type="button"
              onClick={handleSubmit}
              className="mt-[28px] justify-center text-base font-medium"
            >
              Submit
            </Button>
          </div>
        </div>

        <div className="relative flex h-[350px] w-full items-center justify-center overflow-hidden rounded border">
          {previewUrl ? (
            <Image
              src={previewUrl}
              alt="image preview"
              fill
              style={{ objectFit: 'contain' }}
              className="rounded"
              data-testid="layer-image-preview"
            />
          ) : (
            <div className="text-gray-500">No Image</div>
          )}
        </div>
      </div>
    );
  },
);

ElementGlyphForm.displayName = 'ElementGlyphForm';
